import { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useToastStore, type Toast as ToastData, type ToastPosition } from '@/store/toast.store';
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery';

import Button from '../common/Button';
import { IconButton } from '../common/Button';
import FaIcon from './FaIcon';

/* ─── Visual config per toast type ─── */

interface ToastStyle {
  /** Glassmorphic background + text color */
  glass: string;
  /** Left accent border color (4px) */
  accent: string;
  /** Icon color */
  iconColor: string;
  /** FaIcon name */
  iconName: string;
  /** Accessible label */
  ariaLabel: string;
}

const TOAST_STYLES: Record<string, ToastStyle> = {
  success: {
    glass: 'bg-white/90 dark:bg-gray-900/90 text-gray-800 dark:text-gray-100',
    accent: 'border-l-green-500',
    iconColor: 'text-green-600 dark:text-green-400',
    iconName: 'circle-check',
    ariaLabel: 'Success',
  },
  error: {
    glass: 'bg-white/90 dark:bg-gray-900/90 text-gray-800 dark:text-gray-100',
    accent: 'border-l-red-500',
    iconColor: 'text-red-500 dark:text-red-400',
    iconName: 'circle-xmark',
    ariaLabel: 'Error',
  },
  info: {
    glass: 'bg-white/90 dark:bg-gray-900/90 text-gray-800 dark:text-gray-100',
    accent: 'border-l-[#2181a8]',
    iconColor: 'text-[#2181a8] dark:text-[#3ba5d0]',
    iconName: 'circle-info',
    ariaLabel: 'Information',
  },
  warning: {
    glass: 'bg-white/90 dark:bg-gray-900/90 text-gray-800 dark:text-gray-100',
    accent: 'border-l-amber-500',
    iconColor: 'text-amber-600 dark:text-amber-400',
    iconName: 'circle-exclamation',
    ariaLabel: 'Warning',
  },
};

const PRIORITY_ACCENT: Record<string, { accent: string; iconColor: string }> = {
  urgent: { accent: 'border-l-red-500', iconColor: 'text-red-500 dark:text-red-400' },
  high: { accent: 'border-l-orange-500', iconColor: 'text-orange-600 dark:text-orange-400' },
  normal: { accent: 'border-l-[#2181a8]', iconColor: 'text-[#2181a8] dark:text-[#3ba5d0]' },
  low: { accent: 'border-l-gray-400', iconColor: 'text-theme-tertiary' },
};

const POSITION_STYLES: Record<ToastPosition, { className: string; style?: React.CSSProperties }> = {
  'top-right': { className: 'fixed right-4 left-4 sm:left-auto z-[9999] flex flex-col gap-3 pointer-events-none', style: { top: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' } },
  'bottom-left': { className: 'fixed left-4 right-4 sm:right-auto z-[9999] flex flex-col gap-3 pointer-events-none', style: { bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' } },
  'bottom-right': { className: 'fixed right-4 z-[9999] flex flex-col gap-3 pointer-events-none', style: { bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' } },
};

const MAX_VISIBLE = 5;

/* ─── Framer Motion variants (direction-aware) ─── */

function getSlideDirection(position: ToastPosition) {
  return position === 'bottom-left' ? -60 : 60;
}

function getMotionVariants(position: ToastPosition, reducedMotion: boolean) {
  const x = getSlideDirection(position);
  if (reducedMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    };
  }
  return {
    initial: { x, opacity: 0, scale: 0.95 },
    animate: { x: 0, opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 400, damping: 30, mass: 0.8 } },
    exit: { x, opacity: 0, transition: { duration: 0.2, ease: 'easeIn' as const } },
  };
}

/* ─── Shared glass card classes ─── */

const GLASS_BASE = 'backdrop-blur-lg border border-white/20 dark:border-white/10 rounded-xl shadow-xl';

/* ─── Standard Toast ─── */

function StandardToast({ toast }: { toast: ToastData }) {
  const removeToast = useToastStore((s) => s.removeToast);
  const style = TOAST_STYLES[toast.type] || TOAST_STYLES.info;
  return (
    <div
      className={`flex items-start gap-3 px-4 py-3.5 max-w-sm w-full sm:w-96 border-l-4 ${style.accent} ${style.glass} ${GLASS_BASE}`}
      data-toast-card="standard"
    >
      <div className="flex-shrink-0 mt-0.5">
        <FaIcon icon={style.iconName} className={`h-5 w-5 ${style.iconColor}`} />
        <span className="sr-only">{style.ariaLabel}</span>
      </div>
      <div className="flex-1 pt-0.5">
        <p className="text-sm font-medium leading-relaxed">{toast.message}</p>
        {toast.action && (
          <button onClick={(e) => { e.stopPropagation(); toast.action!.onClick(); removeToast(toast.id); }}
            className="mt-1.5 text-sm font-semibold underline hover:no-underline focus:outline-none focus:ring-1 focus:ring-current rounded px-0.5" type="button">
            {toast.action.label}
          </button>
        )}
      </div>
      <button onClick={() => removeToast(toast.id)}
        className="flex-shrink-0 p-1 rounded-lg opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current"
        aria-label="Dismiss notification" type="button">
        <FaIcon icon="xmark" className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ─── Rich Toast (notification-style) ─── */

function RichToast({ toast }: { toast: ToastData }) {
  const removeToast = useToastStore((s) => s.removeToast);
  const priority = toast.priority ?? 'normal';
  const ps = PRIORITY_ACCENT[priority] || PRIORITY_ACCENT.normal;
  const iconName = toast.icon ?? 'bell';
  return (
    <div
      className={`w-80 border-l-4 ${ps.accent} bg-white/90 dark:bg-gray-900/90 text-gray-800 dark:text-gray-100 ${GLASS_BASE} ${toast.onClick ? 'cursor-pointer hover:shadow-card-hover hover:scale-[1.01] transition-all duration-200' : ''}`}
      data-toast-card="rich" onClick={toast.onClick}
    >
      <div className="flex items-start gap-3 px-4 py-3.5">
        <div className="flex-shrink-0 mt-0.5"><FaIcon icon={iconName} className={`h-5 w-5 ${ps.iconColor}`} /></div>
        <div className="flex-1 min-w-0">
          {toast.title && <p className="text-sm font-heading font-semibold text-theme-primary leading-snug">{toast.title}</p>}
          {toast.body && <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{toast.body}</p>}
        </div>
        <IconButton icon={<FaIcon icon="xmark" className="h-4 w-4" />} label="Dismiss notification" variant="ghost" size="sm"
          className="flex-shrink-0 !min-h-0 !min-w-0 !p-1 opacity-70 hover:opacity-100" onClick={(e) => { e.stopPropagation(); removeToast(toast.id); }} />
      </div>
    </div>
  );
}

/* ─── Action Toast (multi-button) ─── */

function ActionToast({ toast }: { toast: ToastData }) {
  const removeToast = useToastStore((s) => s.removeToast);
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
  const [completed, setCompleted] = useState(false);
  const iconName = toast.icon ?? 'bell';
  const handleAction = useCallback(async (index: number) => {
    const action = toast.actions?.[index]; if (!action) return;
    setLoadingIndex(index);
    try { await action.onClick(); setCompleted(true); setTimeout(() => removeToast(toast.id), 1000); }
    catch { setLoadingIndex(null); }
  }, [toast.actions, toast.id, removeToast]);
  return (
    <div
      className={`max-w-md w-full border-l-4 border-l-purple-500 bg-white/90 dark:bg-gray-900/90 text-gray-800 dark:text-gray-100 ${GLASS_BASE}`}
      data-toast-card="action"
    >
      <div className="px-4 py-3.5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900/40 mt-0.5">
            <FaIcon icon={iconName} className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            {completed ? <p className="text-sm font-medium text-green-700 dark:text-green-400">Action completed</p> : (
              <>{toast.title && <p className="text-sm font-heading font-semibold text-theme-primary leading-snug">{toast.title}</p>}
              <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{toast.message}</p></>
            )}
          </div>
          <IconButton icon={<FaIcon icon="xmark" className="h-4 w-4" />} label="Dismiss" variant="ghost" size="sm"
            className="flex-shrink-0 !min-h-0 !min-w-0 !p-1 opacity-70 hover:opacity-100" onClick={() => removeToast(toast.id)} />
        </div>
        {!completed && toast.actions && toast.actions.length > 0 && (
          <div className="flex items-center gap-2 mt-3 ml-11">
            {toast.actions.map((action, index) => (
              <Button key={index} variant={action.variant ?? 'primary'} size="sm" loading={loadingIndex === index} disabled={loadingIndex !== null}
                onClick={(e) => { e.stopPropagation(); handleAction(index); }}>
                {loadingIndex === index && action.loadingLabel ? action.loadingLabel : action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Custom (component) Toast ─── */

function CustomToast({ toast }: { toast: ToastData }) {
  const removeToast = useToastStore((s) => s.removeToast);
  return (
    // A custom component carries no text the announcer can mirror, so it keeps
    // its own polite status role (announced when it is inserted).
    <div role="status" aria-live="polite">
      <div className="relative">{toast.component}
        <button onClick={() => removeToast(toast.id)}
          className="absolute top-1 right-1 p-1 rounded-lg opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current"
          aria-label="Dismiss" type="button">
          <FaIcon icon="xmark" className="h-3.5 w-3.5 text-gray-500" />
        </button>
      </div>
    </div>
  );
}

/* ─── Toast renderer (delegates to correct variant) ─── */

function ToastRenderer({ toast }: { toast: ToastData }) {
  if (toast.component) return <CustomToast toast={toast} />;
  switch (toast.variant) {
    case 'rich': return <RichToast toast={toast} />;
    case 'action': return <ActionToast toast={toast} />;
    default: return <StandardToast toast={toast} />;
  }
}

/* ─── Position group with AnimatePresence ─── */

function PositionGroup({ position, toasts, reducedMotion }: { position: ToastPosition; toasts: ToastData[]; reducedMotion: boolean }) {
  const config = POSITION_STYLES[position];
  const visible = toasts.slice(-MAX_VISIBLE);
  const variants = useMemo(() => getMotionVariants(position, reducedMotion), [position, reducedMotion]);
  return (
    // data-toast-layer: Modal marks every other body child `inert` when it
    // opens. A toast raised while a modal is up mounts afterwards and escapes
    // that snapshot, but one already on screen when the modal opens does not --
    // it would be frozen out of the accessibility tree mid-life. A failed save
    // announcing itself just before the operator opens the next dialog is
    // exactly when that matters.
    <div
      data-toast-layer="true"
      className={config.className}
      style={config.style}
      aria-label="Notifications"
      role="region"
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {visible.map((t, index) => (
          <motion.div
            key={t.id}
            layout
            className="pointer-events-auto"
            initial={variants.initial}
            animate={{ ...variants.animate, transition: { ...(variants.animate as { transition?: object }).transition, delay: reducedMotion ? 0 : index * 0.04 } }}
            exit={variants.exit}
          >
            <ToastRenderer toast={t} />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

/* ─── Container (portal) ─── */

/** Errors and urgent notifications interrupt; everything else waits its turn. */
function isAssertiveToast(t: ToastData): boolean {
  return t.type === 'error' || t.priority === 'urgent';
}

function announcementText(t: ToastData): string {
  const label = (TOAST_STYLES[t.type] || TOAST_STYLES.info).ariaLabel;
  const parts = [t.title, t.message || t.body].filter((p): p is string => typeof p === 'string' && p.length > 0);
  return `${label}: ${parts.join('. ')}`;
}

/**
 * Screen-reader announcer for toasts (WCAG 4.1.3 Status Messages).
 *
 * A live region only announces changes to content it already contains. The old
 * container returned null with no toasts and each card carried its own
 * `role="alert"`, so the region and its text were inserted in the same frame,
 * which NVDA/VoiceOver frequently drop (and every success was an interrupting
 * "alert"). These two regions are mounted permanently, before any toast exists,
 * and only their children change. They sit in the toast layer so an open
 * Modal does not mark them `inert` (inert content is not announced).
 */
function ToastAnnouncer({ toasts }: { toasts: ToastData[] }) {
  const textual = toasts.filter((t) => !t.component);
  return (
    <div data-toast-layer="true" className="sr-only" data-testid="toast-announcer">
      <div role="status" aria-live="polite" aria-atomic="false">
        {textual.filter((t) => !isAssertiveToast(t)).map((t) => (
          <p key={t.id}>{announcementText(t)}</p>
        ))}
      </div>
      <div role="alert" aria-live="assertive" aria-atomic="false">
        {textual.filter(isAssertiveToast).map((t) => (
          <p key={t.id}>{announcementText(t)}</p>
        ))}
      </div>
    </div>
  );
}

export default function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const reducedMotion = usePrefersReducedMotion();
  const groups = toasts.reduce<Record<ToastPosition, ToastData[]>>((acc, t) => {
    const pos = t.position ?? 'bottom-right'; acc[pos].push(t); return acc;
  }, { 'top-right': [], 'bottom-left': [], 'bottom-right': [] });
  return createPortal(
    <>
      <ToastAnnouncer toasts={toasts} />
      {Object.entries(groups).map(([position, positionToasts]) =>
        positionToasts.length > 0 ? <PositionGroup key={position} position={position as ToastPosition} toasts={positionToasts} reducedMotion={reducedMotion} /> : null
      )}
    </>,
    document.body
  );
}
